import { MAP_ANIMATION_SETTINGS, SEARCH_SETTINGS, SEARCH_ZOOM_THRESHOLDS, ZOOM_BEHAVIOR_SETTINGS } from '/settings.js';

// Helper for improved map panning and rendering in Leaflet

export function enhanceMapPanning(map) {
    // Set recommended options for immediate rendering during pan
    map.options.preferCanvas = true;
    map.options.updateWhenIdle = false;
    map.options.trackResize = true;
    map.options.wheelDebounceTime = 40;
    map.options.tap = false;
    // Optionally, you can set more options here as needed
}

export function getOptimizedGeoJsonOptions(baseOptions = {}) {
    // Only set renderer, interactive, pane, and smoothFactor if not already set
    return {
        ...baseOptions,
        renderer: baseOptions.renderer || L.canvas({ padding: 0.5, tolerance: 3 }),
        interactive: typeof baseOptions.interactive !== 'undefined' ? baseOptions.interactive : false,
        pane: baseOptions.pane || 'overlayPane',
        smoothFactor: typeof baseOptions.smoothFactor !== 'undefined' ? baseOptions.smoothFactor : 1
    };
}

export function panToMarkers(map, markers) {
    if (!markers || markers.length === 0) {
        return; // Don't pan if there are no markers
    }

    const group = new L.featureGroup(markers);
    const bounds = group.getBounds();

    // Calculate optimal zoom based on marker distribution
    const optimalZoom = calculateOptimalZoom(bounds, map, markers.length);

    map.flyToBounds(bounds, {
        padding: ZOOM_BEHAVIOR_SETTINGS.BOUNDS_PADDING,
        maxZoom: optimalZoom,
        animate: true,
        duration: MAP_ANIMATION_SETTINGS.PAN_DURATION_SECONDS
    });
}

export function getDynamicMaxZoom(bounds, map) {
    const sw = bounds.getSouthWest();
    const ne = bounds.getNorthEast();
    const distance = map.distance(sw, ne);
    for (const threshold of SEARCH_ZOOM_THRESHOLDS) {
        if (distance <= threshold.maxDistance) {
            return threshold.zoom;
        }
    }
    return ZOOM_BEHAVIOR_SETTINGS.MIN_ZOOM;
}

/**
 * Calculate optimal zoom level based on marker distribution and bounds
 * @param {L.LatLngBounds} bounds - The bounds containing all markers
 * @param {L.Map} map - The map instance
 * @param {number} markerCount - Number of markers to display
 * @returns {number} Optimal zoom level
 */
export function calculateOptimalZoom(bounds, map, markerCount) {
    const sw = bounds.getSouthWest();
    const ne = bounds.getNorthEast();
    const distance = map.distance(sw, ne);

    // Get the base zoom from distance thresholds
    let baseZoom = getDynamicMaxZoom(bounds, map);

    // Special case: single marker - use dedicated zoom level, but do not zoom in closer than SINGLE_MARKER_MAX_ZOOM
    if (markerCount === 1) {
        // Use the minimum of SINGLE_MARKER_ZOOM and SINGLE_MARKER_MAX_ZOOM to avoid over-zooming
        return Math.min(ZOOM_BEHAVIOR_SETTINGS.SINGLE_MARKER_ZOOM, ZOOM_BEHAVIOR_SETTINGS.SINGLE_MARKER_MAX_ZOOM || 13);
    }

    // Calculate area and density for better zoom decisions
    const latSpan = bounds.getNorth() - bounds.getSouth();
    const lngSpan = bounds.getEast() - bounds.getWest();
    const area = latSpan * lngSpan;
    const density = markerCount / Math.max(area, 0.001); // Avoid division by zero

    // Adjust zoom based on marker density
    if (markerCount > 3 && density > ZOOM_BEHAVIOR_SETTINGS.HIGH_DENSITY_THRESHOLD) {
        baseZoom = Math.min(
            baseZoom + ZOOM_BEHAVIOR_SETTINGS.HIGH_DENSITY_ZOOM_BONUS,
            ZOOM_BEHAVIOR_SETTINGS.MAX_ZOOM
        );
    }

    // Use the minimum zoom from settings to ensure we never zoom out further than allowed
    return Math.max(Math.min(baseZoom, ZOOM_BEHAVIOR_SETTINGS.MAX_ZOOM), ZOOM_BEHAVIOR_SETTINGS.MIN_ZOOM);
}

/**
 * Enhanced pan function with better bounds calculation
 * @param {L.Map} map - The map instance
 * @param {Array} markers - Array of L.Marker objects
 * @param {Object} options - Additional options
 */
export function panToMarkersEnhanced(map, markers, options = {}) {
    if (!markers || markers.length === 0) {
        return;
    }

    const {
        padding = ZOOM_BEHAVIOR_SETTINGS.BOUNDS_PADDING,
        animate = true,
        duration = MAP_ANIMATION_SETTINGS.PAN_DURATION_SECONDS,
        maxZoom = null,
        minZoom = ZOOM_BEHAVIOR_SETTINGS.MIN_ZOOM
    } = options;

    const group = new L.featureGroup(markers);
    const bounds = group.getBounds();

    // Calculate optimal zoom
    const optimalZoom = maxZoom || calculateOptimalZoom(bounds, map, markers.length);
    const finalZoom = Math.max(Math.min(optimalZoom, ZOOM_BEHAVIOR_SETTINGS.MAX_ZOOM), minZoom);

    map.flyToBounds(bounds, {
        padding: padding,
        maxZoom: finalZoom,
        animate: animate,
        duration: duration
    });
} 